import { useRouter } from '@tanstack/react-router';
import { useState, useEffect, useCallback } from 'react';

import {
  Stack,
  Button,
  Dialog,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
} from '@mui/material';

import { useAuthContext } from '../hooks';

// ----------------------------------------------------------------------

type RouteConfig = {
  path: string;
  params?: Record<string, string | number>;
};

type RoleRouteResolver = (user: any) => RouteConfig | null;

export type RolesRouteConfig = {
  roleResolvers: Array<{
    roleName: string;
    resolver: RoleRouteResolver;
  }>;
  fallbackRoute: string;
};

type Props = {
  children: React.ReactNode;
  rolesRouteConfig: RolesRouteConfig;
};

export default function RegisterPageGuard({ children, rolesRouteConfig }: Props) {
  const router = useRouter();

  const { authenticated, user, logoutSilent } = useAuthContext();
  const [showDialog, setShowDialog] = useState(false);
  const [showMissingCodeDialog, setShowMissingCodeDialog] = useState(false);

  const handleLogoutAndContinue = async () => {
    await logoutSilent();
    setShowDialog(false);
  };

  const navigateToUserRoute = () => {
    // Find the matching role resolver
    const matchedResolver = rolesRouteConfig.roleResolvers.find(({ roleName }) =>
      user?.roles?.some((role: any) => role.name === roleName)
    );

    if (matchedResolver) {
      const routeConfig = matchedResolver.resolver(user);
      if (routeConfig) {
        router.navigate({
          to: routeConfig.path as any,
          params: routeConfig.params as any,
        });
        return;
      }
    }

    // Fallback route if no role matched
    router.navigate({ to: rolesRouteConfig.fallbackRoute as any });
  };

  const handleStayLoggedIn = () => {
    setShowDialog(false);
    navigateToUserRoute();
  };

  const check = useCallback(() => {
    const registrationCode = new URLSearchParams(window.location.search).get('registrationCode');
    // const registrationEmail = new URLSearchParams(window.location.search).get('email');

    if (!registrationCode) {
      setShowMissingCodeDialog(true);
      return;
    }

    if (authenticated) {
      setShowDialog(true);
    }
  }, [authenticated]);

  useEffect(() => {
    check();
  }, [check]);

  const userName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.email || 'current user';

  return (
    <>
      <Dialog open={showDialog} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle>Already Logged In</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              You&apos;re currently logged in as <strong>{userName}</strong>.
            </Typography>
            <Typography>Would you like to:</Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleStayLoggedIn} variant="outlined">
            Stay logged in
          </Button>
          <Button onClick={handleLogoutAndContinue} variant="contained" color="inherit">
            Log out and register new account
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showMissingCodeDialog} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle>Invalid Registration Link</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>Looks like you&apos;re not supposed to be here.</Typography>
            <Typography>
              Please follow the link from the invitation email in order to register.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={navigateToUserRoute} variant="contained" color="inherit">
            Back
          </Button>
        </DialogActions>
      </Dialog>

      {children}
    </>
  );
}
